import React from 'react';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import Auth from '@Services/Auth';
import UserDatabases from '@Services/UserDatabases';
import * as FormatUtils from '@Services/FormatUtils';
import { shoppingCartStatusCheck } from '@Components/Results/structure/availability/ShoppingCartHelper';
import { formatStructureAvailabilityUrls } from '@Pages/RetroSynthesis/components/RsStructure/RsStructure';
import { RouteType } from '@Pages/RetroSynthesis/constants';

import { FEATURES } from '@Constants/rx-ui/constants';
import { NODE_TYPE_LINK, NODE_TYPE_SUBSTANCE } from '../constants';
import LinkNode from '../components/LinkNode';
import SubstanceNode from '../components/SubstanceNode';
/* eslint-disable import/no-named-as-default-member */
const _getImageDataForPath = (data, path) => {
  const { url: imageUrl, label: imageLabel } = get(data, `substance.path.${path}`, {});
  const { structure: subStructure } = get(data, `substance`, {});
  return {
    url: imageUrl && FormatUtils.formatPath(imageUrl),
    label: imageLabel,
    isZoomAvailable: !!subStructure,
  };
};

function _getHighlightedImageDataForPath(data, target, path) {
  const { url: imageUrl } = get(data, `${target}.path.${path}`, {});
  return imageUrl ? FormatUtils.formatPath(imageUrl) : null;
}

const checkShoppingCartAvailability = async (data) => {
  try {
    const { availability } = data.substance;
    const result =
      availability &&
      (await shoppingCartStatusCheck({
        availability: formatStructureAvailabilityUrls(availability),
        structure: data.substance,
        allowCommercialDBOnly: true,
      }));
    return !!result;
  } catch (e) {
    Logger.error('checkShoppingCartAvailability error:', e);
    return false;
  }
};

// eslint-disable-next-line import/prefer-default-export
export const renderTreeNode = (
  { _type, _data },
  {
    orientation,
    structureImagesPath,
    activeStep,
    setActiveStep,
    setConditionsIsShown,
    onDetailsClick,
    onActiveStepChange,
    nodesDisabled,
    routeType,
    addNodeEnabled,
    onAddNodeClick,
    openLinkOptions,
    displayStepConfidence,
    onHoverSubstanceNode,
    hoveredSubstanceNode,
  } = {}
) => {
  switch (_type) {
    case NODE_TYPE_SUBSTANCE: {
      const { url: imageUrl, label: imageLabel, isZoomAvailable } = _getImageDataForPath(_data, structureImagesPath);
      const supplierNumber = get(_data, 'substance.supplierNumber', null);
      const hasRxn = !isNil(get(_data, 'substance.rxn'));
      const isReaxysDB = UserDatabases.isDefaultDatabase(get(_data, 'substance.databaseId'));
      const viewDetailsEnabled = hasRxn && isReaxysDB;
      const hasStructure = !isNil(get(_data, 'substance.structure'));
      const hasAddNodeIcon = addNodeEnabled && _data && _data.leafNode;
      const substanceDetails = get(_data, 'substance.subDetails', {});
      const customerInfo = get(_data, 'substance.customerInfo', {});
      const parentId = get(_data, 'parentId', null);
      const highlightedImage = _getHighlightedImageDataForPath(_data, 'highlighted', structureImagesPath);
      const highlightedParentImage = _getHighlightedImageDataForPath(_data, 'highlightedTarget', structureImagesPath);
      const id = get(_data, 'id', null);
      const onHoverNode = (isHovered) => {
        onHoverSubstanceNode(isHovered, { id, parentId, highlightedImage, highlightedParentImage });
      };
      let highlightedImageUrl = null;
      if (
        Auth.isFeatureEnabled(FEATURES.enableStructureHighlighting) &&
        hoveredSubstanceNode &&
        hoveredSubstanceNode.highlightedImage &&
        hoveredSubstanceNode.highlightedParentImage &&
        hoveredSubstanceNode.id &&
        hoveredSubstanceNode.parentId &&
        (hoveredSubstanceNode.id === id || hoveredSubstanceNode.parentId === id)
      ) {
        highlightedImageUrl =
          hoveredSubstanceNode.id === id ? hoveredSubstanceNode.highlightedImage : hoveredSubstanceNode.highlightedParentImage;
      }
      return (
        // eslint-disable-next-line react/jsx-filename-extension
        <SubstanceNode
          imageUrl={imageUrl}
          imageLabel={imageLabel}
          orientation={orientation}
          onOptionClick={(event) => onDetailsClick({ ...event, data: _data })}
          checkShoppingCartVisibility={() => checkShoppingCartAvailability(_data)}
          isCurrentStep={_data && (_data.parentStepNumber === activeStep || _data.group === activeStep)}
          buttonsDisabled={nodesDisabled}
          isZoomAvailable={isZoomAvailable}
          hasAddNodeIcon={hasAddNodeIcon}
          addNodeEnabled={hasAddNodeIcon && hasStructure}
          onAddNodeClick={() => onAddNodeClick(_data)}
          supplierNumber={supplierNumber}
          viewDetailsEnabled={viewDetailsEnabled}
          optionsEnabled={hasStructure}
          substanceDetails={substanceDetails}
          onHoverNode={onHoverNode}
          highlightedImageUrl={highlightedImageUrl}
          customerInfo={customerInfo}
        />
      );
    }
    case NODE_TYPE_LINK: {
      const isPredicted = routeType === RouteType.PREDICTED || (routeType === RouteType.CUSTOMIZED && _data.type === RouteType.PREDICTED);
      return (
        <LinkNode
          /* eslint-disable-next-line no-nested-ternary */
          textAbove={isPredicted ? (displayStepConfidence ? _data.confidence : '') : _data.yield}
          step={_data.step}
          stepData={_data}
          orientation={orientation}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          setConditionsIsShown={setConditionsIsShown}
          onClick={({ step, targetRect, stepData }) => {
            if (addNodeEnabled) {
              openLinkOptions({ targetRect, step: stepData });
            } else {
              setActiveStep(step);
              onActiveStepChange(step);
              setConditionsIsShown(true);
            }
          }}
          buttonsDisabled={nodesDisabled}
        />
      );
    }
    default: {
      Logger.error('+++ RT Tree View - Unrecognized tree node type +++', _type, _data);
      return null;
    }
  }
};
